import { useState, useEffect, useRef } from 'react';
import FactReviewBanner from './FactReviewBanner';
import '../../styles/EditCase.css';

function WhyPopover() {
    return (
        <div id="fact-review-why-popover" className="fact-review-why-popover" role="tooltip" aria-label="Why review each fact">
            <h5>Why review each fact?</h5>
            <p>
                AI extraction can mis-attribute or hallucinate values. Verifying each
                fact (and its citation) is the safety net before that data is used to
                draft the HPI and diagnosis.
            </p>
        </div>
    );
}

export default function VerificationBar({
    verificationStats,
    allVerified,
    activeEventProgress,
    onMarkAllCorrect,
    onGenerateAll,
    isGenerating,
    processingTimedOut,
    hasExistingHPI,
    showReGenerateConfirm,
    onCancelReGenerate,
    isAdmin,
    factReviewOnboardingDismissedAt,
    onDismissFactReviewBanner,
    identityBlocked = false,
}) {
    const generateLabel = hasExistingHPI ? 'Regenerate Documents' : 'Generate Documents';
    const [whyOpen, setWhyOpen] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!whyOpen) return;
        const handler = (e) => { if (e.key === 'Escape') setWhyOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [whyOpen]);

    useEffect(() => {
        if (!whyOpen) return;
        const handler = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setWhyOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [whyOpen]);

    const handleDismissBanner = async () => {
        setDismissing(true);
        try {
            await onDismissFactReviewBanner?.();
        } finally {
            setDismissing(false);
        }
    };

    const showBanner = !isAdmin && !factReviewOnboardingDismissedAt;

    return (
        <>
            {showBanner && (
                <FactReviewBanner
                    onDismiss={handleDismissBanner}
                    dismissing={dismissing}
                />
            )}
            <div className="verification-bar">
                <div className="verification-bar-stats">
                    <span className="counter-number">{verificationStats?.verified || 0}</span>
                    <span className="counter-total">/ {verificationStats?.total || 0}</span>
                    <span className="counter-label">facts verified as correct</span>
                    {!isAdmin && (
                        <span className="fact-review-why-wrapper" ref={wrapperRef}>
                            <button
                                type="button"
                                className="fact-review-why-trigger"
                                onClick={() => setWhyOpen((v) => !v)}
                                aria-expanded={whyOpen}
                                aria-controls="fact-review-why-popover"
                            >
                                (ⓘ Why?)
                            </button>
                            {whyOpen && <WhyPopover />}
                        </span>
                    )}
                    {activeEventProgress?.total > 0 && (
                        <span className="verification-bar-events-counter">
                            <span className="counter-number">{activeEventProgress.reviewed}</span>
                            <span className="counter-total">/ {activeEventProgress.total}</span>
                            <span className="counter-label">active care-plan events reviewed</span>
                        </span>
                    )}
                    {activeEventProgress?.total > 0
                        && activeEventProgress.reviewed < activeEventProgress.total && (
                        <span className="verification-bar-events-hint">
                            Confirm or dismiss each active care-plan event to continue.
                        </span>
                    )}
                    {identityBlocked && (
                        <span className="verification-bar-events-hint verification-bar-identity-hint">
                            Patient identity needs review. Confirm the patient in the banner above.
                        </span>
                    )}
                </div>
                <div className="verification-bar-actions">
                    {!allVerified ? (
                        // Mark all Correct cannot open the gate while the
                        // identity flag blocks it, so it is not offered.
                        isAdmin && !identityBlocked ? (
                            <button
                                className="button-orange"
                                onClick={onMarkAllCorrect}
                                type="button"
                            >
                                Mark all Correct
                            </button>
                        ) : null
                    ) : showReGenerateConfirm ? (
                        <div className="confirm-regenerate">
                            <p className="confirm-regenerate-warning">
                                This will replace the existing generated documents.
                            </p>
                            <div className="confirm-regenerate-actions">
                                <button
                                    className="form-button form-button-danger"
                                    type="button"
                                    onClick={() => onGenerateAll({ confirm: true })}
                                >
                                    Confirm Regeneration
                                </button>
                                <button
                                    className="form-button form-button-secondary"
                                    type="button"
                                    onClick={onCancelReGenerate}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <button
                                className="generate-hpi-button"
                                onClick={onGenerateAll}
                                type="button"
                                disabled={isGenerating}
                            >
                                {isGenerating
                                    ? (processingTimedOut ? 'Processing taking longer than expected...' : 'Generating...')
                                    : generateLabel}
                            </button>
                            {processingTimedOut && (
                                <p className="timeout-warning">
                                    Processing is taking longer than expected. You can wait or refresh the page to check status.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
